import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from 'chart.js';

/**
 * ChartRegistry - Centralized Chart.js component registration
 *
 * This module handles the registration of all Chart.js components needed across the application.
 * It includes components for:
 * - Radar charts (existing functionality)
 * - Line charts (for mathematical function plotting)
 * - Shared components (tooltips, legends, etc.)
 *
 * Components registered:
 * - RadialLinearScale: For radar charts
 * - CategoryScale: For x-axis categories in line charts
 * - LinearScale: For linear y-axis in line charts
 * - PointElement: For data points on charts
 * - LineElement: For connecting lines between points
 * - Filler: For area fills under lines
 * - Tooltip: For interactive data tooltips
 * - Legend: For chart legends
 * - Title: For chart titles
 */
export function registerChartComponents(): void {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    RadialLinearScale // Keep for radar charts
  );
}

/**
 * Default chart options shared across all chart types
 * These provide consistent styling and behavior
 */
export const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top' as const,
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: 'white',
      bodyColor: 'white',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1,
    },
  },
};

/**
 * Math-specific chart options for equation plotting
 * These options are optimized for mathematical function visualization
 */
export const mathChartOptions = {
  ...baseChartOptions,
  scales: {
    x: {
      type: 'linear' as const,
      position: 'bottom' as const,
      title: {
        display: true,
        text: 'x',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.1)',
      },
    },
    y: {
      type: 'linear' as const,
      title: {
        display: true,
        text: 'y',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.1)',
      },
    },
  },
  plugins: {
    ...baseChartOptions.plugins,
    tooltip: {
      ...baseChartOptions.plugins?.tooltip,
      callbacks: {
        label: function(context: any) {
          return `(${context.parsed.x.toFixed(2)}, ${context.parsed.y.toFixed(2)})`;
        },
      },
    },
  },
};

/**
 * Color palette for multiple equations
 * Provides distinct colors for different mathematical functions
 */
export const equationColors = [
  {
    backgroundColor: 'rgba(54, 162, 235, 0.2)',
    borderColor: 'rgba(54, 162, 235, 1)',
    pointBackgroundColor: 'rgba(54, 162, 235, 1)',
  },
  {
    backgroundColor: 'rgba(255, 99, 132, 0.2)',
    borderColor: 'rgba(255, 99, 132, 1)',
    pointBackgroundColor: 'rgba(255, 99, 132, 1)',
  },
  {
    backgroundColor: 'rgba(75, 192, 192, 0.2)',
    borderColor: 'rgba(75, 192, 192, 1)',
    pointBackgroundColor: 'rgba(75, 192, 192, 1)',
  },
  {
    backgroundColor: 'rgba(153, 102, 255, 0.2)',
    borderColor: 'rgba(153, 102, 255, 1)',
    pointBackgroundColor: 'rgba(153, 102, 255, 1)',
  },
  {
    backgroundColor: 'rgba(255, 159, 64, 0.2)',
    borderColor: 'rgba(255, 159, 64, 1)',
    pointBackgroundColor: 'rgba(255, 159, 64, 1)',
  },
  {
    backgroundColor: 'rgba(199, 199, 199, 0.2)',
    borderColor: 'rgba(199, 199, 199, 1)',
    pointBackgroundColor: 'rgba(199, 199, 199, 1)',
  },
];

// Initialize the chart components on module load
registerChartComponents();